
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Pencil, Lock } from "lucide-react";
import { EditPlanDialog } from "./EditPlanDialog";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Plan {
  id: string;
  feature_id: string;
  name: string;
  price: number;
  duration: number;
  type: string;
  status: string;
  products: {
    name: string;
  } | null;
}

interface PlanCardProps {
  plan: Plan;
  onDelete: (id: string) => void;
  onUpdate: () => void;
}

export const PlanCard = ({ plan, onDelete, onUpdate }: PlanCardProps) => {
  const [hasActiveSubscriptions, setHasActiveSubscriptions] = useState(false);

  useEffect(() => {
    const checkActiveSubscriptions = async () => {
      const { data: activeSubscriptions, error } = await supabase
        .from("subscriptions")
        .select("id")
        .eq("plan_id", plan.id)
        .neq("status", "cancelled");

      if (!error && activeSubscriptions) {
        setHasActiveSubscriptions(activeSubscriptions.length > 0);
      }
    };

    checkActiveSubscriptions();
  }, [plan.id]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="space-y-1">
        <div className="flex justify-end gap-2">
          <EditPlanDialog
            plan={plan}
            onPlanUpdated={onUpdate}
          />
          {hasActiveSubscriptions ? (
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-500 cursor-not-allowed"
              title="Plan is protected - has active subscriptions"
            >
              <Lock className="h-4 w-4" />
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the plan "{plan.name}".
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(plan.id)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        <CardTitle className="text-2xl text-center">
          {plan.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center space-y-4">
          <p className="text-2xl font-bold text-primary">
            {formatPrice(plan.price)}
          </p>
          <div className="text-sm text-gray-600">
            <p>Duration: {plan.duration} days</p>
            <p>Type: {plan.type}</p>
            <p>Status: {plan.status}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
